import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useToaster } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { IApiResponse, IUserDto } from '../../../utils/constants/interfaces';
import { PermissionType } from '../../../utils/constants/permissions';
import { ToastStyle } from '../../../utils/hooks/useToaster';

interface IUserEditorHook {
  user: IUserDto;
  isSaving: boolean;
  fieldErrors: string[];
  loadingUser: boolean;
  showUnsavedChangesDialog: boolean;
  showSetPasswordModal: boolean;
  isSendingEmail: boolean;
  sendFirstTimeEmail: () => Promise<void>;
  back: () => void;
  updateValue: (key: string, value: string | boolean | string[] | null) => void;
  togglePermission: (permission: PermissionType) => void;
  saveChanges: () => Promise<void>;
  deleteUser: () => Promise<void>;
  discardUnsavedChanges: () => void;
  closeUnsavedChangesDialog: () => void;
  openSetPasswordModal: () => void;
  closeSetPasswordModal: () => void;
  onPasswordSetSuccess: () => void;
  ToastContainer: React.ComponentType;
}

const defaultUser = {
  id: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  cellPhone: '',
  permissions: [],
  createdBy: '',
  lastModifiedBy: ''
} as unknown as IUserDto;

const useUserEditor = (userId: string | null): IUserEditorHook => {
  const api = useApi();
  const navigate = useNavigate();
  const toaster = useToaster();
  const [user, setUser] = useState<IUserDto>(defaultUser);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [loadingUser, setLoadingUser] = useState<boolean>(true);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState<boolean>(false);

  const closeUnsavedChangesDialog = () => {
    setShowUnsavedChangesDialog(false);
  };

  const openSetPasswordModal = () => {
    setShowSetPasswordModal(true);
  };

  const closeSetPasswordModal = () => {
    setShowSetPasswordModal(false);
  };

  const onPasswordSetSuccess = () => {
    setShowSetPasswordModal(false);
  };

  const discardUnsavedChanges = () => {
    setShowUnsavedChangesDialog(false);
    setIsDirty(false);
    setUser(defaultUser);
    void loadUser(userId);
  };

  const back = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
      return;
    }
    setUser(defaultUser);
    void navigate('/users');
  };

  const loadUser = useCallback(
    async (userId: string | null): Promise<void> => {
      if (!Helpers.isNullOrWhiteSpace(userId)) {
        if (userId === 'new') {
          setUser(defaultUser);
          setIsDirty(false);
        } else {
          setFieldErrors([]);
          setLoadingUser(true);
          const endpoint = `/users/${userId}`;
          const apiResponse = await api.get<IUserDto>(endpoint);
          if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
            setUser(apiResponse.response);
          } else {
            await navigate('/users?error+loading+user');
          }
        }
        setLoadingUser(false);
      }
    },
    [api, navigate]
  );

  const updateValue = (key: string, value: string | boolean | string[] | null): void => {
    if (isSaving || !user) return;
    setIsDirty(true);

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    const editUser = { ...user };
    editUser[key] = value;
    setUser(editUser);
  };

  const togglePermission = (permission: PermissionType): void => {
    if (isSaving || !user) return;
    setIsDirty(true);

    const editUser = { ...user };
    const permissions = [...(editUser.permissions || [])];

    if (permissions.includes(permission)) {
      editUser.permissions = permissions.filter((p) => p !== permission);
    } else {
      permissions.push(permission);
      editUser.permissions = permissions;
    }

    setUser(editUser);
  };

  const validate = () => {
    const errors: string[] = [];
    //overview
    if (Helpers.isNullOrWhiteSpace(user.emailAddress) || !Helpers.isValidEmail(user.emailAddress)) {
      errors.push('overview:emailAddress');
    }
    if (Helpers.isNullOrWhiteSpace(user.firstName)) {
      errors.push('overview:firstName');
    }
    if (Helpers.isNullOrWhiteSpace(user.lastName)) {
      errors.push('overview:lastName');
    }

    setFieldErrors(errors);
    return errors.length === 0;
  };

  const saveChanges = async (): Promise<void> => {
    if (isSaving) return;

    const isValid = validate();
    if (!isValid) {
      toaster.addToast(`Unable to save — required fields are missing.`, ToastStyle.ERROR, 5000);
      Helpers.scrollToClassName('is-invalid', -20);
      return;
    }
    const emptyUserReplacer = (key: string, value: string | string[] | boolean | null | Date) => {
      // Filtering out properties
      if (
        key === 'id' &&
        ['string', 'null', 'undefined'].includes(typeof value) &&
        (Helpers.isNullOrWhiteSpace(value as string | null | undefined) || value === 'new')
      ) {
        return undefined;
      }
      return value;
    };

    setIsSaving(true);
    const saveRequest = JSON.stringify(user, emptyUserReplacer);
    let apiResponse: IApiResponse<IUserDto>;
    let newUser = true;
    if (Helpers.isNullOrWhiteSpace(user.id)) {
      const endpoint = `/users`;
      apiResponse = await api.post<IUserDto>(endpoint, saveRequest);
    } else {
      const endpoint = `/users/${user.id}`;
      apiResponse = await api.put<IUserDto>(endpoint, saveRequest);
      newUser = false;
    }
    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      setIsDirty(false);
      toaster.addToast(`User saved successfully!`, ToastStyle.SUCCESS, 2000);
      // navigate to new
      if (newUser) {
        await navigate(`/users/${apiResponse.response.id}`);
      }
      setUser(apiResponse.response);
    } else {
      toaster.addToast(`Error saving user`, ToastStyle.ERROR, 8000);
    }
    setIsSaving(false);
  };

  const sendFirstTimeEmail = async (): Promise<void> => {
    if (isSaving || user.emailConfirmed) return;
    setIsSendingEmail(true);
    const endpoint = `/users/${user.id}/first-time-email`;
    const apiResponse = await api.post<IUserDto>(endpoint, '');
    if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
      setIsDirty(false);
      toaster.addToast(`Email Sent!`, ToastStyle.SUCCESS, 2000);
    } else {
      toaster.addToast(`Error sending email`, ToastStyle.ERROR, 8000);
    }
    setIsSendingEmail(false);
  };

  const deleteUser = async (): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);
    const endpoint = `/users/${user.id}`;
    const apiResponse = await api.delete<boolean>(endpoint);
    if (apiResponse?.status?.statusCode === 200 && apiResponse.response === true) {
      await navigate('/users');
    } else {
      toaster.addToast(`Error deleting user`, ToastStyle.ERROR, 8000);
    }
    setIsSaving(false);
  };

  useEffect(() => {
    void loadUser(userId);
  }, [loadUser, userId]);

  return {
    user,
    isSaving,
    fieldErrors,
    loadingUser,
    showUnsavedChangesDialog,
    showSetPasswordModal,
    isSendingEmail,
    sendFirstTimeEmail,
    back,
    updateValue,
    togglePermission,
    saveChanges,
    deleteUser,
    discardUnsavedChanges,
    closeUnsavedChangesDialog,
    openSetPasswordModal,
    closeSetPasswordModal,
    onPasswordSetSuccess,
    ToastContainer: toaster.ToastContainer
  };
};

export default useUserEditor;
